import { ImportType, type SqlImport } from '@/services/db/sql-import/entity';
import type { ObjectType } from '@/services/db/object-type/entity';

/** The name an object import is grouped under when its object type is not in the workspace's list. */
const UNKNOWN_OBJECT_GROUP = 'object';

export interface SqlImportGroup<T extends SqlImport> {
  /** The heading, without the trailing "Sync". */
  name: string;
  imports: T[];
}

/**
 * The list page groups imports by what they write to: people first, then one
 * group per object type in the order the imports introduce them.
 */
export function groupSqlImportsBySyncTarget<T extends SqlImport>(
  sqlImports: readonly T[],
  objectTypes: readonly Pick<ObjectType, 'id' | 'name'>[],
  peopleTitlePlural: string,
): SqlImportGroup<T>[] {
  const objectTypeNames = new Map(objectTypes.map((objectType) => [objectType.id, objectType.name]));
  const people: T[] = [];
  const objects = new Map<string, T[]>();

  for (const sqlImport of sqlImports) {
    if (sqlImport.import_type !== ImportType.object) {
      people.push(sqlImport);
      continue;
    }
    const name = objectTypeNames.get(sqlImport.object_type_id ?? '') ?? UNKNOWN_OBJECT_GROUP;
    const group = objects.get(name) ?? [];
    group.push(sqlImport);
    objects.set(name, group);
  }

  const groups: SqlImportGroup<T>[] = [];
  if (people.length > 0) {
    groups.push({ name: peopleTitlePlural, imports: people });
  }
  for (const [name, imports] of objects) {
    groups.push({ name, imports });
  }
  return groups;
}
